import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, AuditItem } from '@/lib/supabase';

// ============================================================================
// Hook: useAuditItems - Récupère tous les audit items d'un restaurant
// ============================================================================
export const useAuditItems = (restaurantId: string) => {
  return useQuery({
    queryKey: ['audit-items', restaurantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_items')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('priority', { ascending: true })
        .order('ice_score', { ascending: false });

      if (error) throw error;
      return data as AuditItem[];
    },
    enabled: !!restaurantId,
  });
};

// ============================================================================
// Hook: useAuditItemsByCategory - Récupère les items par catégorie
// ============================================================================
export const useAuditItemsByCategory = (
  restaurantId: string,
  category: 'technique' | 'semantique' | 'local'
) => {
  return useQuery({
    queryKey: ['audit-items', restaurantId, category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_items')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('category', category)
        .order('priority', { ascending: true })
        .order('ice_score', { ascending: false });

      if (error) throw error;
      return data as AuditItem[];
    },
    enabled: !!restaurantId,
  });
};

// ============================================================================
// Hook: useAuditStats - Récupère les statistiques d'audit
// ============================================================================
export const useAuditStats = (restaurantId: string) => {
  return useQuery({
    queryKey: ['audit-stats', restaurantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_items')
        .select('status, priority, category')
        .eq('restaurant_id', restaurantId);

      if (error) throw error;

      const items = data as AuditItem[];
      
      return {
        total: items.length,
        completed: items.filter(i => i.status === 'completed').length,
        in_progress: items.filter(i => i.status === 'in_progress').length,
        todo: items.filter(i => i.status === 'todo').length,
        blocked: items.filter(i => i.status === 'blocked').length,
        byPriority: {
          P0: items.filter(i => i.priority === 'P0').length,
          P1: items.filter(i => i.priority === 'P1').length,
          P2: items.filter(i => i.priority === 'P2').length,
        },
        byCategory: {
          technique: items.filter(i => i.category === 'technique').length,
          semantique: items.filter(i => i.category === 'semantique').length,
          local: items.filter(i => i.category === 'local').length,
        },
        completionRate: items.length > 0 
          ? Math.round((items.filter(i => i.status === 'completed').length / items.length) * 100)
          : 0,
      };
    },
    enabled: !!restaurantId,
  });
};

// ============================================================================
// Hook: useUpdateAuditItem - Met à jour un audit item
// ============================================================================
export const useUpdateAuditItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<AuditItem>;
    }) => {
      const { data, error } = await supabase
        .from('audit_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as AuditItem;
    },
    onSuccess: (data) => {
      // Invalider le cache pour recharger les données
      queryClient.invalidateQueries({ queryKey: ['audit-items', data.restaurant_id] });
      queryClient.invalidateQueries({ queryKey: ['audit-stats', data.restaurant_id] });
    },
  });
};

// ============================================================================
// Hook: useToggleAuditStatus - Toggle le statut d'un item (todo <-> completed)
// ============================================================================
export const useToggleAuditStatus = () => {
  const updateMutation = useUpdateAuditItem();

  return useMutation({
    mutationFn: async (item: AuditItem) => {
      const newStatus = item.status === 'completed' ? 'todo' : 'completed';
      const updates: Partial<AuditItem> = {
        status: newStatus,
        completed_date: newStatus === 'completed' ? new Date().toISOString() : null,
      };

      return updateMutation.mutateAsync({ id: item.id, updates });
    },
  });
};

// ============================================================================
// Hook: useCreateAuditItem - Crée un nouvel audit item
// ============================================================================
export const useCreateAuditItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (auditItem: Partial<AuditItem>) => {
      const { data, error } = await supabase
        .from('audit_items')
        .insert(auditItem)
        .select()
        .single();

      if (error) throw error;
      return data as AuditItem;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['audit-items', data.restaurant_id] });
      queryClient.invalidateQueries({ queryKey: ['audit-stats', data.restaurant_id] });
    },
  });
};

// ============================================================================
// Hook: useDeleteAuditItem - Supprime un audit item
// ============================================================================
export const useDeleteAuditItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, restaurantId }: { id: string; restaurantId: string }) => {
      const { error } = await supabase
        .from('audit_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, restaurantId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['audit-items', data.restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['audit-stats', data.restaurantId] });
    },
  });
};
